import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import style from "./style.module.css";
import { usePermissions } from "../../hooks/usePermissions";
import { useCookies } from "react-cookie";
import { authenticatedFetch } from "../../utils/api";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { API_BASE } from "../../config/api";
import { formatCurrency } from "../../config/currency";
import Pagination from "../../Components/Pagination";
import OrderChatPanel from "../../Components/OrderChatPanel";
import { usePrintReceipt } from "../../utils/usePrintReceipt";
import { parseApiError } from "../../utils/parseApiError";
import { usePosSettings, getPosSettings } from "../../utils/posSettings";

// Lets handleGenerateBill's catch block show the real status (401/403/404/
// ...) a failed fetch returned, instead of a hardcoded generic one.
class HttpError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

interface Order {
  order_id: number;
  user_id: number;
  address_id: number;
  order_date: string;
  delivery_date: string;
  order_type: string;
  total_amount: number;
  payment_method: string;
  payment_status: string;
  order_status: string;
  created_at: string;
  updated_at: string;
  // Related data
  user_name?: string;
  order_address?: {
    address_name: string;
    address1: string;
    address2: string;
    city: string;
    country: string;
  } | null;
  order_items?: OrderItem[];
  order_payments?: OrderPayment[];
  order_statuses?: OrderStatus[];
  order_slot?: OrderSlotInfo | null;
}

interface OrderItem {
  order_item_id: number;
  order_id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  product_image?: string;
  discount_id?: number;
}

interface OrderPayment {
  order_payment_id: number;
  order_id: number;
  payment_method: string;
  payment_status: string;
  created_at: string;
}

interface OrderStatus {
  order_status_id: number;
  order_id: number;
  status_name: string;
  created_at: string;
}

interface OrderSlotInfo {
  start_time: string;
  end_time: string;
  slot_name: string | null;
}

interface DeliverySlot {
  delivery_slot_id: number;
  slot_name: string | null;
  start_time: string;
  end_time: string;
}

// Shared by the Slot filter's option labels and the Slot column's cell
// text — a slot's name takes priority when set, else its time range.
const formatSlotLabel = (slot: { slot_name?: string | null; start_time: string; end_time: string }) =>
  slot.slot_name || `${slot.start_time.slice(0, 5)}–${slot.end_time.slice(0, 5)}`;


const OrderPage = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const { printingId: printingBillOrderId, isPrinting: isPrintingAnyBill, print: doPrintBill } = usePrintReceipt();
  const [orderItems, setOrderItems] = useState<OrderItem[]>([]);
  const [orderPayments, setOrderPayments] = useState<OrderPayment[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<OrderStatus[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [orderTypeFilter, setOrderTypeFilter] = useState<"" | "delivery" | "pickup">("");
  const [slotFilter, setSlotFilter] = useState("");
  const [sortBy, setSortBy] = useState<"order_date" | "delivery_date" | "created_at">("order_date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [allSlots, setAllSlots] = useState<DeliverySlot[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showProductsModal, setShowProductsModal] = useState(false);
  const [selectedOrderForProducts, setSelectedOrderForProducts] = useState<number | null>(null);
  const [isLoadingOrderItems, setIsLoadingOrderItems] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedOrderForStatus, setSelectedOrderForStatus] = useState<number | null>(null);
  const [currentOrderStatus, setCurrentOrderStatus] = useState<string>("");
  const [currentPaymentStatus, setCurrentPaymentStatus] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatOrderId, setChatOrderId] = useState<number | null>(null);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [selectedOrderForVoid, setSelectedOrderForVoid] = useState<number | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [isVoiding, setIsVoiding] = useState(false);
  const permissions = usePermissions();
  const [cookies] = useCookies();
  const snack = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();

  // Lets NotificationBell (and anywhere else) deep-link into a specific
  // order: ?chat=<order_id> opens that order's chat panel directly (for a
  // chat-message notification), ?search=<order_id> filters the table down
  // to it via the existing order-ID search (for an order-status
  // notification) — reusing what's already there rather than adding a
  // dedicated single-order view. Cleared immediately after being applied so
  // it doesn't keep re-triggering on every subsequent render/navigation.
  //
  // Depends on searchParams (not mount-only) — NotificationBell is rendered
  // persistently in the sidebar, visible on this page too, so a staff
  // member already on /order clicking a second notification only changes
  // the URL's query string without unmounting OrderPage. A mount-only
  // effect would silently miss that; this one re-fires on the change. The
  // clear-after-apply (setSearchParams({})) doesn't loop: it only fires
  // when a param was actually present, and the resulting empty params on
  // the next run have nothing left to act on.
  useEffect(() => {
    const chatParam = searchParams.get('chat');
    const searchParam = searchParams.get('search');
    if (chatParam) {
      setChatOrderId(Number(chatParam));
    }
    if (searchParam) {
      setSearchTerm(searchParam);
    }
    if (chatParam || searchParam) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Debounce the search box so it doesn't refetch on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // A new search term or filter/sort selection invalidates the current
  // page — go back to page 1.
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, orderTypeFilter, slotFilter, sortBy, sortDir]);

  useEffect(() => {
    fetchOrderPayments();
    fetchOrderStatuses();
    fetchUsers();
    fetchAllSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, debouncedSearchTerm, orderTypeFilter, slotFilter, sortBy, sortDir, cookies.auth]);

  const fetchAllSlots = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/deliveryslot`, {
        method: 'GET'
      }, cookies.auth);
      if (response.ok) {
        const result = await response.json();
        setAllSlots(Array.isArray(result.data) ? result.data : []);
      }
    } catch (error) {
      console.error("Error fetching delivery slots:", error);
    }
  }, [cookies.auth]);

  const settings = usePosSettings();
  const pageSize = settings.itemsPerPage || 25;

  const fetchOrders = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(pageSize), sort_by: sortBy, sort_dir: sortDir });
      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (orderTypeFilter) params.set('order_type', orderTypeFilter);
      if (slotFilter) params.set('delivery_slot_id', slotFilter);
      const response = await authenticatedFetch(`${API_BASE}/order?${params}`, {
        method: 'GET'
      }, cookies.auth);
      if (response.ok) {
        const result = await response.json();
        setOrders(Array.isArray(result.data) ? result.data : []);
        setTotalPages(result.totalPages || 1);
        setTotalItems(result.total || 0);
      } else {
        console.error("Failed to fetch orders. Status:", response.status);
        setOrders([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setOrders([]);
    }
  }, [cookies.auth, page, pageSize, debouncedSearchTerm, orderTypeFilter, slotFilter, sortBy, sortDir]);

  // Order items are no longer preloaded for every order on the page — only
  // fetched for the one order the "View Products" modal is currently open
  // for. Preloading all items for all orders was the single biggest
  // unbounded fetch on this page and doesn't scale with order history.
  const fetchOrderItemsForOrder = useCallback(async (orderId: number) => {
    setIsLoadingOrderItems(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/orderitem?order_id=${orderId}`, {
        method: 'GET'
      }, cookies.auth);
      if (response.ok) {
        const result = await response.json();
        setOrderItems(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error("Failed to fetch order items. Status:", response.status);
        setOrderItems([]);
      }
    } catch (error) {
      console.error("Error fetching order items:", error);
      setOrderItems([]);
    } finally {
      setIsLoadingOrderItems(false);
    }
  }, [cookies.auth]);

  const fetchOrderPayments = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/orderpayment`, {
        method: 'GET'
      }, cookies.auth);
      
      if (response.ok) {
        const data = await response.json();
        setOrderPayments(data || []);
      } else {
        console.error("Failed to fetch order payments. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching order payments:", error);
    }
  }, [cookies.auth]);

  const fetchOrderStatuses = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/orderstatus`, {
        method: 'GET'
      }, cookies.auth);
      
      if (response.ok) {
        const data = await response.json();
        setOrderStatuses(data || []);
      } else {
        console.error("Failed to fetch order statuses. Status:", response.status);
      }
    } catch (error) {
      console.error("Error fetching order statuses:", error);
    }
  }, [cookies.auth]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await authenticatedFetch(`${API_BASE}/user/users?page=1&limit=100`, {
        method: 'GET'
      }, cookies.auth);

      if (response.ok) {
        const data = await response.json();
        // Handle different response formats
        if (Array.isArray(data)) {
          setUsers(data);
        } else if (data && Array.isArray(data.users)) {
          setUsers(data.users);
        } else {
          console.error("Unexpected users response format:", data);
          setUsers([]);
        }
      } else {
        console.error("Failed to fetch users. Status:", response.status);
        setUsers([]);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    }
  }, [cookies.auth]);

  const toggleOrderExpansion = (orderId: number) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleManageStatus = (orderId: number) => {
    const order = orders.find(o => o.order_id === orderId);
    if (order) {
      setSelectedOrderForStatus(orderId);
      const latestStatus = getLatestOrderStatus(orderId);
      setCurrentOrderStatus(latestStatus === 'N/A' ? 'confirming' : latestStatus);
      const latestPayment = getLatestPaymentStatus(orderId);
      setCurrentPaymentStatus(latestPayment === 'N/A' ? 'pending' : latestPayment);
      setShowStatusModal(true);
    }
  };

  const handleVoidSale = async () => {
    if (!selectedOrderForVoid || !voidReason.trim()) return;
    try {
      setIsVoiding(true);
      const res = await authenticatedFetch(
        `${API_BASE}/order/${selectedOrderForVoid}/void`,
        { method: "POST", body: JSON.stringify({ reason: voidReason.trim() }) },
        cookies.auth
      );
      if (res.ok) {
        setShowVoidModal(false);
        setSelectedOrderForVoid(null);
        setVoidReason("");
        await fetchOrders();
        await fetchOrderStatuses();
        await fetchOrderPayments();
        snack.onResponse({ message: "Sale voided.", status: 200 });
      } else {
        snack.onResponse({ message: await parseApiError(res, "Failed to void sale"), status: res.status });
      }
    } catch (error) {
      console.error("Error voiding sale:", error);
      snack.onResponse({ message: "Error voiding sale", status: 500 });
    } finally {
      setIsVoiding(false);
    }
  };

  const handleGenerateBill = async (orderId: number) => {
    try {
      // The order-detail fetch lives inside this thunk rather than being
      // awaited before calling doPrintBill — printReceipt (via the hook)
      // owns the ordering of USB device acquisition vs. this fetch vs. the
      // browser-print fallback's window.open(), all of which are time-
      // sensitive in different ways. See receiptPrinter.ts's printReceipt.
      const result = await doPrintBill(String(orderId), async () => {
        const response = await authenticatedFetch(`${API_BASE}/order/${orderId}/detail`, {
          method: 'GET'
        }, cookies.auth);
        if (!response.ok) {
          // Carries the real status (401/403/404/...) through to the catch
          // block below — previously lost the moment this became a plain
          // Error, so a permissions error and a deleted order both showed
          // the same generic "500" badge in the snackbar regardless of
          // what actually went wrong.
          throw new HttpError("Failed to load order details for bill", response.status);
        }
        const detail = await response.json();
        const addressLine = detail.address
          ? [detail.address.address_name, [detail.address.address1, detail.address.address2].filter(Boolean).join(', '), [detail.address.city, detail.address.country].filter(Boolean).join(', ')]
              .filter(Boolean)
              .join(' — ')
          : undefined;

        const configuredStoreName = getPosSettings().storeName?.trim();
        const cachedStoreName = typeof window !== "undefined" && window.localStorage ? (localStorage.getItem("pos_store_name") || "") : "";
        const storeName = configuredStoreName || cachedStoreName || undefined;

        return {
          orderId: detail.order.order_id,
          timestamp: new Date(detail.order.order_date || detail.order.created_at),
          cashier: `${cookies.auth?.first_name ?? ""} ${cookies.auth?.last_name ?? ""}`.trim() || "Staff",
          orderType: detail.order.order_type,
          items: (detail.items || []).map((item: OrderItem) => ({
            product_name: item.product_name,
            unit_price: item.unit_price,
            quantity: item.quantity,
          })),
          subtotal: (detail.items || []).reduce((sum: number, item: OrderItem) => sum + item.unit_price * item.quantity, 0),
          discountAmount: Number(detail.order.discount_amount || 0),
          total: detail.order.total_amount,
          paymentMethod: detail.payment?.payment_method ?? 'N/A',
          customerName: detail.customer?.name,
          address: addressLine,
          storeName,
        };
      });
      // result is null only when another bill was already printing —
      // doPrintBill's own guard, nothing to report.
      if (result && !result.ok) {
        snack.onResponse({ message: result.message || "Failed to generate the bill", status: 400 });
      }
    } catch (error) {
      console.error("Error generating bill:", error);
      snack.onResponse({
        message: error instanceof Error ? error.message : "Error generating bill",
        status: error instanceof HttpError ? error.status : 500,
      });
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrderForStatus) return;

    try {
      setIsLoading(true);

      // Update order status — PUT if record exists, POST if not
      const existingStatus = orderStatuses.find(s => s.order_id === selectedOrderForStatus);
      const orderStatusResponse = existingStatus
        ? await authenticatedFetch(`${API_BASE}/orderstatus/${existingStatus.order_status_id}`, {
            method: "PUT",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status_name: currentOrderStatus })
          }, cookies.auth)
        : await authenticatedFetch(`${API_BASE}/orderstatus`, {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: selectedOrderForStatus, status_name: currentOrderStatus })
          }, cookies.auth);

      // Update payment status — PUT if record exists, POST if not
      const existingPayment = orderPayments.find(p => p.order_id === selectedOrderForStatus);
      const paymentStatusResponse = existingPayment
        ? await authenticatedFetch(`${API_BASE}/orderpayment/${existingPayment.order_payment_id}`, {
            method: "PUT",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ payment_status: currentPaymentStatus })
          }, cookies.auth)
        : await authenticatedFetch(`${API_BASE}/orderpayment`, {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: selectedOrderForStatus, payment_method: getLatestPaymentMethod(selectedOrderForStatus) || 'cash', payment_status: currentPaymentStatus })
          }, cookies.auth);

      if (orderStatusResponse.ok && paymentStatusResponse.ok) {
        setShowStatusModal(false);
        setSelectedOrderForStatus(null);
        setCurrentOrderStatus("");
        setCurrentPaymentStatus("");
        await fetchOrders();
        await fetchOrderStatuses();
        await fetchOrderPayments();
        snack.onResponse({
          message: "Order status updated successfully!",
          status: 200,
        });
      } else {
        snack.onResponse({
          message: "Failed to update order status",
          status: 500,
        });
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      snack.onResponse({
        message: "Error updating order status",
        status: 500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "Invalid Date";
    }
  };

  const getUserName = (userId: number) => {
    if (!Array.isArray(users)) return `User ${userId}`;
    const user = users.find(u => u.user_id === userId);
    return user ? `${user.user_first_name} ${user.user_last_name}` : `User ${userId}`;
  };

  const formatAddress = (order: Order) => {
    const addr = order.order_address;
    if (!addr) return 'N/A';
    const line = [addr.address1, addr.address2].filter(Boolean).join(', ');
    const cityCountry = [addr.city, addr.country].filter(Boolean).join(', ');
    return [addr.address_name, line, cityCountry].filter(Boolean).join(' — ');
  };

  const getOrderItems = (orderId: number) => {
    if (!Array.isArray(orderItems)) return [];
    return orderItems.filter(item => item.order_id === orderId);
  };

  const getOrderPayments = (orderId: number) => {
    if (!Array.isArray(orderPayments)) return [];
    return orderPayments.filter(payment => payment.order_id === orderId);
  };

  const getOrderStatuses = (orderId: number) => {
    if (!Array.isArray(orderStatuses)) return [];
    return orderStatuses.filter(status => status.order_id === orderId);
  };

  const getLatestOrderStatus = (orderId: number) => {
    const statuses = getOrderStatuses(orderId);
    if (statuses.length === 0) return 'N/A';
    // Sort by created_at descending and get the latest
    const latest = statuses.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    return latest.status_name;
  };

  const getLatestPaymentStatus = (orderId: number) => {
    const payments = getOrderPayments(orderId);
    if (payments.length === 0) return 'N/A';
    // Sort by created_at descending and get the latest
    const latest = payments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    return latest.payment_status;
  };

  const getLatestPaymentMethod = (orderId: number) => {
    const payments = getOrderPayments(orderId);
    if (payments.length === 0) return 'N/A';
    // Sort by created_at descending and get the latest
    const latest = payments.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
    return latest.payment_method;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return style.statusCompleted;
      case 'confirming':
        return style.statusPending;
      case 'confirmed':
        return style.statusConfirmed;
      case 'packing':
        return style.statusPacking;
      case 'ready':
        return style.statusReady;
      case 'out_for_delivery':
        return style.statusShipped;
      case 'cancelled':
        return style.statusCancelled;
      default:
        return style.statusDefault;
    }
  };

  const getPaymentStatusBadgeClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'completed':
        return style.paymentPaid;
      case 'pending':
        return style.paymentPending;
      case 'failed':
      case 'cancelled':
        return style.paymentFailed;
      default:
        return style.paymentDefault;
    }
  };

  // Search, filtering, and ordering all happen server-side (see
  // fetchOrders) — `orders` is already exactly the current page's rows,
  // already filtered and sorted per orderTypeFilter/slotFilter/sortBy/sortDir.

  return (
    <div className={style.wrapper}>
      {/* Header Section */}
      <div className={style.header}>
        <h1 className={style.title}>Orders</h1>
        <div className={style.controlsRow}>
          <div className={style.searchSection}>
            <input
              type="text"
              placeholder="Search by order ID or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={style.searchInput}
            />
          </div>

          {/* Filter bar — always visible, not behind a toggle */}
          <div className={style.filterBar}>
            <label className={style.filterLabel}>
              Order Type
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value as "" | "delivery" | "pickup")}
                className={style.filterSelect}
              >
                <option value="">All</option>
                <option value="delivery">Delivery</option>
                <option value="pickup">Pickup</option>
              </select>
            </label>
            <label className={style.filterLabel}>
              Slot
              <select
                value={slotFilter}
                onChange={(e) => setSlotFilter(e.target.value)}
                className={style.filterSelect}
              >
                <option value="">All</option>
                {allSlots.map((slot) => (
                  <option key={slot.delivery_slot_id} value={slot.delivery_slot_id}>{formatSlotLabel(slot)}</option>
                ))}
              </select>
            </label>
            <label className={style.filterLabel}>
              Sort By
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "order_date" | "delivery_date" | "created_at")}
                className={style.filterSelect}
              >
                <option value="order_date">Order Date</option>
                <option value="delivery_date">Delivery Date</option>
                <option value="created_at">Created At</option>
              </select>
            </label>
            <button
              type="button"
              className={style.sortDirBtn}
              onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
              title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
            >
              {sortDir === 'asc' ? '▲ Asc' : '▼ Desc'}
            </button>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className={style.tableContainer}>
        <table className={style.dataTable}>
          <colgroup>
            <col style={{ width: '100px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '160px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '130px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '90px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '100px' }} />
          </colgroup>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>User</th>
              <th>Address</th>
              <th>Order Date</th>
              <th>Delivery Date</th>
              <th>Slot</th>
              <th>Order Type</th>
              <th>Total Amount</th>
              <th>Payment Method</th>
              <th>Payment Status</th>
              <th>Order Status</th>
              <th>Products</th>
              <th>Bill</th>
              <th>Manage Status</th>
              <th>Chat</th>
              <th>Void</th>
              <th>Created At</th>
              <th>Updated At</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order, idx) => (
              <React.Fragment key={`${order.order_id}-${idx}`}>
                <tr className={style.tableRow}>
                  <td>{order.order_id}</td>
                  <td>{getUserName(order.user_id)}</td>
                  <td>{formatAddress(order)}</td>
                  <td>{formatDate(order.order_date)}</td>
                  <td>{formatDate(order.delivery_date)}</td>
                  <td>{order.order_slot ? formatSlotLabel(order.order_slot) : '—'}</td>
                    <td>
                      <span className={`${style.orderType} ${style[order.order_type]}`}>
                        {order.order_type}
                      </span>
                    </td>
                  <td>{formatCurrency(order.total_amount)}</td>
                  <td>{getLatestPaymentMethod(order.order_id)}</td>
                  <td>
                    <span className={`${style.statusBadge} ${getPaymentStatusBadgeClass(getLatestPaymentStatus(order.order_id))}`}>
                      {getLatestPaymentStatus(order.order_id)}
                    </span>
                  </td>
                  <td>
                    <span className={`${style.statusBadge} ${getStatusBadgeClass(getLatestOrderStatus(order.order_id))}`}>
                      {getLatestOrderStatus(order.order_id)}
                    </span>
                  </td>
                  <td>
                    <button
                      className={style.viewProductsBtn}
                      onClick={() => {
                        setSelectedOrderForProducts(order.order_id);
                        setShowProductsModal(true);
                        fetchOrderItemsForOrder(order.order_id);
                      }}
                    >
                      View Products
                    </button>
                  </td>
                  <td>
                    <button
                      className={style.viewProductsBtn}
                      onClick={() => handleGenerateBill(order.order_id)}
                      disabled={isPrintingAnyBill}
                      style={{backgroundColor: '#059669'}}
                    >
                      {printingBillOrderId === String(order.order_id) ? "Printing…" : "Generate Bill"}
                    </button>
                  </td>
                  <td>
                    {permissions.canEdit ? (
                      <button
                        className={style.viewProductsBtn}
                        onClick={() => handleManageStatus(order.order_id)}
                        style={{backgroundColor: '#919191'}}
                      >
                        Manage Status
                      </button>
                    ) : (
                      <span style={{color: '#aaa', fontSize: '0.8rem', fontStyle: 'italic'}}>View only</span>
                    )}
                  </td>
                  <td>
                    <button
                      className={style.viewProductsBtn}
                      onClick={() => setChatOrderId(order.order_id)}
                      style={{backgroundColor: '#2563eb'}}
                    >
                      Chat
                    </button>
                  </td>
                  <td>
                    {(permissions.isOwner || permissions.isAdmin) ? (
                      getLatestOrderStatus(order.order_id) === 'cancelled' ? (
                        <span style={{color: '#aaa', fontSize: '0.8rem', fontStyle: 'italic'}}>Voided</span>
                      ) : (
                        <button
                          className={style.viewProductsBtn}
                          onClick={() => { setSelectedOrderForVoid(order.order_id); setVoidReason(""); setShowVoidModal(true); }}
                          style={{backgroundColor: '#dc2626'}}
                        >
                          Void Sale
                        </button>
                      )
                    ) : (
                      <span style={{color: '#aaa', fontSize: '0.8rem', fontStyle: 'italic'}}>—</span>
                    )}
                  </td>
                  <td>{formatDate(order.created_at)}</td>
                  <td>{formatDate(order.updated_at)}</td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={totalItems} />

      {/* Products Modal */}
      {showProductsModal && selectedOrderForProducts && (
        <div className={style.modalOverlay}>
          <div className={style.productsModal}>
            <h2>Order Items for Order #{selectedOrderForProducts}</h2>
            {isLoadingOrderItems ? (
              <p className={style.noProductsMessage}>Loading...</p>
            ) : getOrderItems(selectedOrderForProducts).length > 0 ? (
              <div className={style.productsContainer}>
                {getOrderItems(selectedOrderForProducts).map((item) => (
                  <div key={item.order_item_id} className={style.productRow}>
                    <div className={style.productDetails}>
                      <div className={style.formGroup}>
                        <label>Product ID:</label>
                        <div className={style.formValue}>{item.product_id}</div>
                      </div>
                      <div className={style.formGroup}>
                        <label>Discount ID:</label>
                        <div className={style.formValue}>{item.discount_id ?? 'null'}</div>
                      </div>
                      <div className={style.formGroup}>
                        <label>Product Name:</label>
                        <div className={style.formValue}>{item.product_name || 'N/A'}</div>
                      </div>
                      <div className={style.formGroup}>
                        <label>Quantity:</label>
                        <div className={style.formValue}>{item.quantity}</div>
                      </div>
                      <div className={style.formGroup}>
                        <label>Unit Price:</label>
                        <div className={style.formValue}>{formatCurrency(item.unit_price)}</div>
                      </div>
                      <div className={style.formGroup}>
                        <label>Total Price:</label>
                        <div className={style.formValueTotal}>{formatCurrency(item.total_price)}</div>
                      </div>
                    </div>
                    <div className={style.productImageContainer}>
                      {item.product_image ? (
                        <img 
                          src={item.product_image} 
                          alt={item.product_name}
                          className={style.productImage}
                        />
                      ) : (
                        <div className={style.noImagePlaceholder}>
                          <span>No Image</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {getOrderItems(selectedOrderForProducts).length > 0 && (
                  <div className={style.totalSummary}>
                    <div className={style.totalRow}>
                      <span className={style.totalLabel}>Total Order Amount:</span>
                      <span className={style.totalAmount}>
                        {formatCurrency(
                          getOrderItems(selectedOrderForProducts).reduce(
                            (sum, item) => sum + item.total_price, 0
                          )
                        )}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className={style.noProductsMessage}>No products found for this order.</p>
            )}
            <div className={style.modalButtons}>
              <button onClick={() => { setShowProductsModal(false); setOrderItems([]); }} className={style.cancelBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Management Modal */}
      {showStatusModal && selectedOrderForStatus && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Manage Order Status - Order #{selectedOrderForStatus}</h2>
            
            <div className={style.formGroup}>
              <label>Order Status:</label>
              <select
                value={currentOrderStatus}
                onChange={(e) => setCurrentOrderStatus(e.target.value)}
                className={style.modalInput}
                required
              >
                <option value="confirming">Waiting for Confirmation</option>
                <option value="confirmed">Confirmed</option>
                <option value="packing">Packing</option>
                <option value="ready">Ready</option>
                <option value="out_for_delivery">Out for Delivery</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className={style.formGroup}>
              <label>Payment Status:</label>
              <select
                value={currentPaymentStatus}
                onChange={(e) => setCurrentPaymentStatus(e.target.value)}
                className={style.modalInput}
                required
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="failed">Failed</option>
                <option value="refunded">Refunded</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>

            <div className={style.modalButtons}>
              <button 
                onClick={() => {
                  setShowStatusModal(false);
                  setSelectedOrderForStatus(null);
                  setCurrentOrderStatus("");
                  setCurrentPaymentStatus("");
                }} 
                className={style.cancelBtn}
              >
                Cancel
              </button>
              {permissions.canEdit && (
                <button
                  onClick={handleUpdateStatus}
                  className={style.saveBtn}
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update Status'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Void Sale Modal */}
      {showVoidModal && selectedOrderForVoid && (
        <div className={style.modalOverlay}>
          <div className={style.modal}>
            <h2>Void Sale - Order #{selectedOrderForVoid}</h2>
            <p style={{color: '#555', fontSize: '0.9rem'}}>
              This cancels the order and marks its payment as voided. This cannot be undone.
            </p>

            <div className={style.formGroup}>
              <label>Reason:</label>
              <input
                type="text"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                className={style.modalInput}
                placeholder="e.g. Rang up the wrong item"
                required
              />
            </div>

            <div className={style.modalButtons}>
              <button
                onClick={() => { setShowVoidModal(false); setSelectedOrderForVoid(null); setVoidReason(""); }}
                className={style.cancelBtn}
              >
                Cancel
              </button>
              <button
                onClick={handleVoidSale}
                className={style.saveBtn}
                disabled={isVoiding || !voidReason.trim()}
                style={{backgroundColor: '#dc2626'}}
              >
                {isVoiding ? 'Voiding...' : 'Void Sale'}
              </button>
            </div>
          </div>
        </div>
      )}

      {chatOrderId !== null && (
        <OrderChatPanel orderId={chatOrderId} onClose={() => setChatOrderId(null)} />
      )}

    </div>
  );
};

export default OrderPage;